<template>
    <UIcon
        v-bind="forwardedAttrs"
        :name="name"
        :size="size"
        :mode="iconMode"
        :class="iconClass"
        :style="iconStyle"
    />
</template>

<script setup lang="ts">
import { normalizeClass } from 'vue'
import type { StyleValue } from 'vue'

/**
 * Wrapper around Nuxt UI `UIcon` with an optional typed **`strokeWidth`** prop.
 *
 * **Why it exists:** `UIcon` has no `strokeWidth` prop; Lucide SVGs use baked-in `stroke-width` on
 * `<g>` / shapes. This component sets `--icon-stroke-width` on the root so `main.css` can apply
 * one rule for all Lucide icons (same variable as `icon-stroke-*` utilities).
 *
 * **How stroke works:** This component sets the CSS custom property `--icon-stroke-width` on the
 * icon root. Global rules in `layers/base/assets/css/main.css` apply
 * `stroke-width: var(--icon-stroke-width, 2)` to Lucide shapes (`svg.iconify--lucide …`). If the
 * variable is unset, stroke falls back to **2** (Lucide default).
 *
 * **Nuxt registration:** File `components/shared/AppIcon.vue` is auto-imported as **`SharedAppIcon`**
 * (path prefix `shared/` → `Shared*`).
 *
 * **When to use plain `UIcon` instead:** Icons that do not need a custom stroke, or when you only
 * set `--icon-stroke-width` via utilities (`icon-stroke-3`, `[--icon-stroke-width:1.67]`) on the
 * same element as the icon (e.g. `UButton` `ui.leadingIcon`).
 *
 * @example Basic icon (inherits `app.config` `icon.mode`, typically `svg`)
 * ```vue
 * <SharedAppIcon name="lucide:mail" class="size-6 text-(--ui-text-primary)" />
 * ```
 *
 * @example Explicit size and stroke (forces `mode="svg"` while stroke is set)
 * ```vue
 * <SharedAppIcon name="lucide:scroll-text" :size="42" :stroke-width="1.67" />
 * ```
 *
 * @example Forwarding listeners / extra props (non-class/style attrs pass through to `UIcon`)
 * ```vue
 * <SharedAppIcon name="lucide:x" class="size-5" aria-hidden="true" @click="onClick" />
 * ```
 *
 * @see `doc/guide/GUIDE_icons.md` for `IconBlock`, utilities, and `UButton` patterns.
 */
const props = defineProps<{
    /** Iconify / Nuxt Icon name (e.g. `lucide:info`, `i-lucide-mail`). */
    name: string
    /** Passed to `UIcon` (`font-size` / dimensions depend on Nuxt Icon defaults). */
    size?: string | number
    /**
     * Optional `UIcon` render mode when `strokeWidth` is unset. Omit to use `app.config` `icon.mode`.
     */
    mode?: 'svg' | 'css'
    /**
     * Stroke width for Lucide icons (user units in the 24×24 viewBox). Sets `--icon-stroke-width`
     * on the root; see `main.css`. Forces `mode="svg"` so CSS-mask icons still expose SVG shapes.
     */
    strokeWidth?: string | number
}>()

const attrs = useAttrs()

const hasCustomStroke = computed(
    () =>
        props.strokeWidth !== undefined &&
        props.strokeWidth !== null &&
        `${props.strokeWidth}`.trim() !== ''
)

const iconMode = computed((): 'svg' | 'css' | undefined => {
    if (hasCustomStroke.value) return 'svg'
    return props.mode
})

const forwardedAttrs = computed(() => {
    const { class: _class, style: _style, ...rest } = attrs
    return rest
})

const iconClass = computed(() => {
    const classes: unknown[] = []
    if (attrs.class !== undefined && attrs.class !== null) {
        classes.push(attrs.class)
    }
    return normalizeClass(classes)
})

const iconStyle = computed<StyleValue>(() => {
    const styles: StyleValue[] = []
    if (attrs.style) {
        styles.push(attrs.style as StyleValue)
    }
    if (hasCustomStroke.value) {
        styles.push({
            '--icon-stroke-width': `${props.strokeWidth}`,
        })
    }
    return styles.length ? styles : undefined
})
</script>
